import { type CheckedState } from '@radix-ui/react-checkbox';
import { useEffect, useState } from 'react';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import PwHideIcon from '@/assets/icons/PwHideIcon.svg?react';
import PwSeeIcon from '@/assets/icons/PwSeeIcon.svg?react';
import { Card } from '@/components/atoms/Card';
import { Dialog, DialogContent, DialogTrigger } from '@/components/atoms/Dialog';
import Input from '@/components/atoms/Input';
import { Button } from '@/components/atoms/NewPcButton';
import TextField from '@/components/molecules/TextField';
import UnregisterDialogContainer from '@/containers/market/UnregisterDialogContainer';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { MARKET_TEXTBOX_OPTIONAL_LIST } from '@/pages/Market/_constants/const';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';
import { CplatApiResponse } from '@/types/api';
import { useQueryClient } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import UpdateMarketAlias from './UpdateMarketAlias';
type FormData = {
  newAlias: string;
};

type TermsFormData = {
  Key: string;
  GuideMessage: string;
  ReferenceMessage: string;
  ReferenceUrl: string;
  IsInputMandatory: boolean;
  values: CheckedState;
};

export type FormInput = ({
  viewEncryptIcon?: boolean;
  hasToBeEncrypted?: boolean;
  name: string;
} & React.InputHTMLAttributes<HTMLInputElement>)[];

type FormCardProps = {
  inputList: FormInput;
  className?: string;
  errors?:
    | (Record<
        string,
        Partial<{
          type: string | number;
          message: string;
        }>
      > &
        Partial<{
          type: string | number;
          message: string;
        }>)
    | undefined;
  agreementList?: TermsFormData[];
  isLoading?: boolean;
  disabled?: boolean;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  onCheckedChange?: (name: string, checked: CheckedState) => void;
  onClickMoreTerms?: (name: string) => void;
  onRememberPassword?: () => void;
};

// TODO 마켓등록, 비밀번호 변경, 마켓요청 등 form card 추상화 하기
const FormCard = (props: FormCardProps) => {
  const {
    className,
    marketName,
    marketNameEnglish,
    marketIconUrl,
    marketId,
    inputList,
    errors,
    agreementList,
    disabled,
    isLoading,
    onSubmit,
    onCheckedChange,
    onClickMoreTerms,
    onRememberPassword,
    isAliasEnable,
    currentAlias,
    vendorId,
    checkedMarket,
    registeredMarket,
  } = props;
  const user = useAppSelector((state) => state.user);
  const { showToast } = useToast();

  const [hideList, setHideList] = useState(
    inputList
      .filter((item) => item.hasToBeEncrypted)
      .reduce(
        (acc, { name }) => ({
          ...acc,
          [name]: true,
        }),
        {} as { [key: string]: boolean },
      ),
  );
  const [alias, setAlias] = useState(currentAlias || '');

  useEffect(() => {
    setHideList(
      inputList
        .filter((item) => item.hasToBeEncrypted)
        .reduce(
          (acc, { name }) => ({
            ...acc,
            [name]: true,
          }),
          {} as { [key: string]: boolean },
        ),
    );
  }, [inputList]);

  const queryClient = useQueryClient();
  const { mutate: updateMarketAlias, isPending } = usePost(CPLAT_API_ROUTES.updateMarketAlias);
  const { setUserEventHistory } = useLog(user.UserNo);

  const { register, handleSubmit, watch, formState } = useForm<FormData>({
    defaultValues: { newAlias: currentAlias },
  });

  const handleChangeAlias: SubmitHandler<FormData> = () => {
    const { CplatToken } = user;

    const body = {
      CplatToken,
      MarketName: marketNameEnglish,
      StoreId: marketNameEnglish === 'coupang' ? vendorId : marketId,
      originMarketAlias: currentAlias,
      toBeMarketAlias: alias,
    };

    updateMarketAlias(body, {
      onSuccess(res) {
        const { code } = res.data as CplatApiResponse<undefined>;
        if (code === '200') {
          // queryClient.refetchQueries({ queryKey: [CPLAT_API_ROUTES.getRegisteredMarketInfo] });
          showToast('success', '마켓별칭이 변경되었습니다.');
          setUserEventHistory({
            eventName: 'update_alias',
            eventValue: alias,
          });
        } else if (code === '722') {
          showToast('warning', '동일한 마켓별칭이 존재합니다. 다른 별칭으로 다시 시도해 주세요');
        } else {
          showToast('warning', '마켓별칭 변경에 실패했습니다. 다시 시도해 주세요');
        }
      },
      onError(err) {
        showToast(
          'warning',
          `예상치 못한 에러가 발생했습니다. 다시 시도해 주십시오. 에러코드: ${err.code}`,
        );
      },
    });
  };

  const formValues = watch();
  const isDisable = isPending || currentAlias === formValues.newAlias;

  const [unRegisterDialogOpen, setUnRegisterDialogOpen] = useState(false);
  const navigate = useNavigate();
  const handleUnregisterDialogClose = () => {
    setUnRegisterDialogOpen(false);
    navigate(ROUTES.market);
  };

  const showRememberPasswordButton =
    inputList.length > 1 &&
    marketNameEnglish !== 'smartstore' &&
    inputList.some(
      (item) =>
        item.name === 'MarketPassword' || item.name === 'SecretKey' || item.name === 'AccessKey',
    ) &&
    typeof onRememberPassword === 'function';
  const marketNameLabel = checkedMarket?.MarketAlias || checkedMarket?.MarketId || marketName;

  return (
    <Card className=" px-[20px] py-[24px]">
      <div className="flex flex-col gap-4">
        <div className="mb-[16px] flex items-center gap-3">
          {marketIconUrl && (
            <img src={marketIconUrl} alt={`${marketNameEnglish} 아이콘`} className="h-6 w-6" />
          )}
          <h2 className="text-gr-900 Body1S16">{`${marketNameLabel}`}</h2>
        </div>
      </div>
      <form onSubmit={onSubmit}>
        <div className="relative flex flex-col gap-4">
          {marketNameEnglish === 'smartstore' ? (
            // 스마트스토어: ID만 노출
            <div className="flex items-center gap-2">
              <label className="flex-1 text-gr-800 Body2M16">ID</label>
              <div className="flex w-[400px] items-center gap-2">
                <Input
                  value={marketId}
                  disabled
                  readOnly
                  variant="register"
                  className="h-[40px] Body5M14"
                />
              </div>
            </div>
          ) : (
            // 그 외 마켓: inputList 전체 노출
            inputList.map(({ hasToBeEncrypted, viewEncryptIcon, ...props }) => {
              // const label = MARKET_TEXTBOX_OPTIONAL_LIST[props.name]?.title || props.name;
              let label;
              if (props.placeholder === 'API 키를 입력하세요.') {
                label = 'API 키';
              } else if (props.placeholder === '이메일 주소를 입력하세요.') {
                label = '이메일';
              } else if (props.placeholder === '셀러 ID를 입력해 주세요.') {
                label = '셀러 ID';
              } else if (props.placeholder === 'Access key를 입력하세요.') {
                label = 'AccessKey';
              } else if (props.placeholder === 'Secret key를 입력하세요.') {
                label = 'SecretKey';
              } else {
                label = MARKET_TEXTBOX_OPTIONAL_LIST[props.name]?.title || props.name;
              }

              // 비밀번호 불러오기 버튼 노출 조건
              const showRememberPasswordButton =
                ((props.name === 'MarketPassword' && registeredMarket?.MarketPassword) ||
                  (props.name === 'SecretKey' && registeredMarket?.SecretKey) ||
                  (props.name === 'AccessKey' && registeredMarket?.AccessKey)) &&
                typeof onRememberPassword === 'function';

              return (
                <div key={props.name} className="flex items-center gap-2">
                  <label className={`w-[100px]  text-gr-800 Body2M16`}>
                    {label}
                    <span className="ml-1 text-red-400">*</span>
                  </label>
                  <div className="flex flex-1 items-center gap-2">
                    <div className="w-full flex-1">
                      {viewEncryptIcon ? (
                        <TextField
                          {...props}
                          type={hideList[props.name] ? 'password' : 'text'}
                          variant="register"
                          className="h-[40px] Body5M14"
                          InputProps={{
                            endAdornment: hideList[props.name] ? (
                              <PwHideIcon
                                onClick={() =>
                                  setHideList({ ...hideList, [props.name]: !hideList[props.name] })
                                }
                                className="cursor-pointer"
                              />
                            ) : (
                              <PwSeeIcon
                                onClick={() =>
                                  setHideList({ ...hideList, [props.name]: !hideList[props.name] })
                                }
                                className="cursor-pointer"
                              />
                            ),
                          }}
                        />
                      ) : (
                        <Input {...props} variant="register" className="h-[40px] Body5M14" />
                      )}
                    </div>
                    {showRememberPasswordButton && (
                      <Button
                        type="button"
                        size="xsmall"
                        className="h-[34px] w-[122px]"
                        onClick={() => onRememberPassword(props.name)}
                        variant="muted"
                      >
                        비밀번호 불러오기
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {isAliasEnable && (
          <UpdateMarketAlias
            marketNameEnglish={marketNameEnglish}
            vendorId={vendorId}
            marketId={marketId}
            currentAlias={currentAlias}
          />
        )}

        <div className="mt-[16px] flex items-center gap-3 ">
          <Dialog open={unRegisterDialogOpen} onOpenChange={setUnRegisterDialogOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                className="flex-1"
                variant="muted"
                onClick={() => setUnRegisterDialogOpen(true)}
              >
                연동 해제
              </Button>
            </DialogTrigger>
            <DialogContent
              noCloseBtn
              className="max-w-[320px] p-[30px]"
              data-membership-preview-interactive="true"
            >
              <UnregisterDialogContainer
                onClose={handleUnregisterDialogClose}
                checkedMarket={checkedMarket}
              />
            </DialogContent>
          </Dialog>
          {showRememberPasswordButton && (
            <Button type="submit" className="flex-1" isLoading={isLoading} disabled={disabled}>
              재연동
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};

export default FormCard;

/**
 * 수정해야 할 정보를 체크하는 함수
 * 대부분의 마켓이 MarketId, MarketPassword 이지만
 * 쿠팡의 경우 MarketId -> VendorId
 * MarketPassword -> SecretKey ,AccessKey 이다.
 */
const checkReadonlyValue = (key: string) => {
  switch (key) {
    case 'MarketPassword':
    case 'SecretKey':
    case 'AccessKey':
      return false;
    default:
      return true;
  }
};
